import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product, ProductCost } from '../types';
import { productService } from '../services/productService';
import { authService } from '../services/authService';
import { statsService } from '../services/statsService';
import { supabase } from '../services/supabase';

interface ProductFormData {
  name: string;
  description: string;
  price: number;
  image_url: string;
  category: 'merch' | 'drinks' | 'packs';
  stock: number;
  is_pack: boolean;
  pack_items?: { id: string; quantity: number }[];
}

const ProductForm = ({ 
  onSubmit, 
  onCancel, 
  initialData 
}: { 
  onSubmit: (data: ProductFormData) => void;
  onCancel: () => void;
  initialData?: Product;
}) => {
  const [formData, setFormData] = useState<ProductFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    price: initialData?.price || 0,
    image_url: initialData?.image_url || '',
    category: initialData?.category || 'merch',
    stock: initialData?.stock || 0,
    is_pack: initialData?.is_pack || false,
    pack_items: initialData?.pack_items || []
  });

  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>(
    initialData?.pack_items?.reduce((acc, item) => ({ ...acc, [item.id]: item.quantity }), {}) || {}
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await productService.getAllProducts();
        // Filtrer les produits qui ne sont pas des packs
        setAvailableProducts(products.filter(p => !p.is_pack));
      } catch (err) {
        console.error('Erreur lors de la récupération des produits:', err);
      }
    };

    if (formData.category === 'packs') {
      fetchProducts();
    }
  }, [formData.category]);

  const handleItemSelection = (productId: string, quantity: number) => {
    setSelectedItems(prev => ({
      ...prev,
      [productId]: quantity
    }));
  };

  const calculateBasePrice = () => {
    return Object.entries(selectedItems).reduce((total, [productId, quantity]) => {
      const product = availableProducts.find(p => p.id === productId);
      return total + (product?.price || 0) * quantity;
    }, 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.category === 'packs') {
      const packItems = Object.entries(selectedItems)
        .filter(([_, quantity]) => quantity > 0)
        .map(([id, quantity]) => ({ id, quantity }));
      
      // Assurer qu'un prix valide est défini
      const basePrice = calculateBasePrice();
      const defaultPackPrice = basePrice > 0 ? basePrice * 0.9 : 0; // 10% de réduction par défaut
      
      onSubmit({
        ...formData,
        is_pack: true,
        pack_items: packItems,
        price: isNaN(formData.price) || formData.price <= 0 ? defaultPackPrice : formData.price
      });
    } else {
      onSubmit({
        ...formData,
        is_pack: false,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border border-gray-200 rounded-lg p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nom</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            rows={3}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Prix (€)</label>
          <input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">URL de l'image</label>
          <input
            type="url"
            value={formData.image_url}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Catégorie</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as 'merch' | 'drinks' | 'packs' })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            required
          >
            <option value="merch">Merchandising</option>
            <option value="drinks">Forfaits Boissons</option>
            <option value="packs">Packs</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Stock</label>
          <input
            type="number"
            value={formData.stock}
            onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) })}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
            required
          />
        </div>
      </div>

      {formData.category === 'packs' && (
        <div className="border border-gray-200 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-medium">Articles du pack</h3>
          <div className="space-y-4">
            {availableProducts.map(product => (
              <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-4">
                  <input
                    type="checkbox"
                    checked={!!selectedItems[product.id]}
                    onChange={(e) => handleItemSelection(product.id, e.target.checked ? 1 : 0)}
                    className="h-4 w-4 text-pink-500 focus:ring-pink-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="font-medium">{product.name}</span>
                    <span className="text-gray-500 ml-2">{product.price}€</span>
                  </div>
                </div>
                {selectedItems[product.id] > 0 && (
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => handleItemSelection(product.id, Math.max(0, (selectedItems[product.id] || 0) - 1))}
                      className="px-2 py-1 bg-gray-200 rounded"
                    >
                      -
                    </button>
                    <span>{selectedItems[product.id]}</span>
                    <button
                      type="button"
                      onClick={() => handleItemSelection(product.id, (selectedItems[product.id] || 0) + 1)}
                      className="px-2 py-1 bg-gray-200 rounded"
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between">
              <span className="font-medium">Prix de base (articles séparés):</span>
              <span>{calculateBasePrice().toFixed(2)}€</span>
            </div>
            <div className="flex justify-between mt-2">
              <span className="font-medium">Prix du pack:</span>
              <input
                type="number"
                step="0.01"
                value={isNaN(formData.price) || formData.price <= 0 ? '' : formData.price}
                onChange={(e) => {
                  const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setFormData({ ...formData, price: isNaN(value) ? 0 : value });
                }}
                className="w-32 px-2 py-1 border border-gray-300 rounded"
                min={0}
                placeholder="0.00"
              />
            </div>
            {calculateBasePrice() > 0 && formData.price > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                Économie: {Math.round((1 - formData.price / calculateBasePrice()) * 100)}%
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600"
        >
          {initialData ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

const ProductCosts = ({ 
  product, 
  onClose 
}: { 
  product: Product;
  onClose: () => void;
}) => {
  const [costs, setCosts] = useState<ProductCost[]>(product.costs || []);
  const [newCost, setNewCost] = useState({ quantity: '', cost: '' });
  const [error, setError] = useState<string | null>(null);
  const [totalSold, setTotalSold] = useState<{ paid: number; pending: number; inPacks: number }>({ paid: 0, pending: 0, inPacks: 0 });
  const [loading, setLoading] = useState(true);
  const [simulationSales, setSimulationSales] = useState<number>(totalSold.paid);

  useEffect(() => {
    const fetchProductStats = async () => {
      try {
        const stats = await statsService.getProductStats();
        const productStats = stats.topProducts.find(p => p.id === product.id);
        
        // Récupérer les ventes via les packs
        const { data: packItems } = await supabase
          .from('pack_items')
          .select('*');

        let totalInPacks = 0;
        if (packItems) {
          packItems.forEach(pack => {
            const item = pack.items.find(item => item.id === product.id);
            if (item) {
              // Récupérer les ventes du pack
              const packSales = stats.topProducts.find(p => p.id === pack.pack_id)?.paidSold || 0;
              totalInPacks += item.quantity * packSales;
            }
          });
        }

        setTotalSold({
          paid: productStats?.paidSold || 0,
          pending: productStats?.pendingSold || 0,
          inPacks: totalInPacks
        });
      } catch (err) {
        console.error('Erreur lors de la récupération des statistiques:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductStats();
  }, [product.id]);

  const handleAddCost = async () => {
    try {
      const quantity = parseInt(newCost.quantity);
      const cost = parseFloat(newCost.cost);
      
      if (isNaN(quantity) || isNaN(cost) || quantity <= 0 || cost <= 0) {
        setError('Veuillez entrer des valeurs valides');
        return;
      }

      const updatedProduct = await productService.addProductCost(product.id, { quantity, cost });
      setCosts(updatedProduct.costs);
      setNewCost({ quantity: '', cost: '' });
      setError(null);
    } catch (err) {
      setError('Erreur lors de l\'ajout du coût');
    }
  };

  const handleRemoveCost = async (quantity: number) => {
    try {
      const updatedProduct = await productService.removeProductCost(product.id, quantity);
      setCosts(updatedProduct.costs);
    } catch (err) {
      setError('Erreur lors de la suppression du coût');
    }
  };

  const optimalQuantity = productService.getOptimalPurchaseQuantity(costs, totalSold.paid, product.price);

  // Trouver le prochain seuil de rentabilité non atteint
  const nextBreakEvenPoint = costs
    .map(cost => ({
      quantity: cost.quantity,
      breakEvenPoint: Math.ceil(cost.cost / product.price)
    }))
    .sort((a, b) => a.breakEvenPoint - b.breakEvenPoint)
    .find(cost => cost.breakEvenPoint > totalSold.paid);

  // Calculer la rentabilité de chaque lot
  const calculateProfitability = (cost: ProductCost) => {
    const breakEvenPoint = Math.ceil(cost.cost / product.price);
    const unitCost = cost.cost / cost.quantity;
    const unitProfit = product.price - unitCost;
    return (totalSold.paid - breakEvenPoint) * unitProfit;
  };

  // Trouver le lot le plus rentable
  const lotsWithProfitability = costs
    .map(cost => ({
      quantity: cost.quantity,
      cost: cost.cost,
      breakEvenPoint: Math.ceil(cost.cost / product.price),
      profitability: calculateProfitability(cost)
    }))
    .sort((a, b) => b.profitability - a.profitability);

  const mostProfitableLot = lotsWithProfitability[0];

  // Calculer la rentabilité simulée
  const calculateSimulatedProfitability = (lot: typeof lotsWithProfitability[0], sales: number) => {
    if (sales < lot.breakEvenPoint) return 0;
    const unitCost = lot.cost / lot.quantity;
    const unitProfit = product.price - unitCost;
    
    // Calculer le nombre de lots nécessaires
    const numberOfLots = Math.ceil(sales / lot.quantity);
    const totalCost = numberOfLots * lot.cost;
    
    // Calculer le bénéfice total
    const totalRevenue = sales * product.price;
    return totalRevenue - totalCost;
  };

  // Trouver le lot le plus rentable pour une simulation donnée
  const getMostProfitableLotForSales = (sales: number) => {
    return lotsWithProfitability
      .map(lot => ({
        ...lot,
        simulatedProfitability: calculateSimulatedProfitability(lot, sales)
      }))
      .sort((a, b) => b.simulatedProfitability - a.simulatedProfitability)[0];
  };

  // Calculer les ventes nécessaires pour qu'un lot devienne le plus rentable
  const calculateRequiredSales = (lot: typeof lotsWithProfitability[0]) => {
    if (lot.profitability >= mostProfitableLot.profitability) return null;
    
    const unitCost = lot.cost / lot.quantity;
    const unitProfit = product.price - unitCost;
    const mostProfitableUnitCost = mostProfitableLot.cost / mostProfitableLot.quantity;
    const mostProfitableUnitProfit = product.price - mostProfitableUnitCost;
    
    // Trouver le point où la rentabilité du lot actuel dépasse celle du lot le plus rentable
    let sales = totalSold.paid;
    let currentLotProfitability = calculateProfitability(lot);
    let mostProfitableLotProfitability = mostProfitableLot.profitability;
    
    while (currentLotProfitability <= mostProfitableLotProfitability) {
      sales++;
      currentLotProfitability = calculateSimulatedProfitability(lot, sales);
      mostProfitableLotProfitability = calculateSimulatedProfitability(mostProfitableLot, sales);
    }
    
    return sales;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Gestion des coûts de production</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Statistiques de ventes</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <p className="text-gray-700">
                Ventes payées: <span className="font-semibold">{totalSold.paid}</span> unités
              </p>
              <p className="text-gray-500">
                Ventes en attente: <span className="font-semibold">{totalSold.pending}</span> unités
              </p>
              <p className="text-blue-600">
                Ventes via packs: <span className="font-semibold">{totalSold.inPacks}</span> unités
              </p>
              <p className="text-gray-900 font-medium">
                Total des ventes: <span className="font-semibold">{totalSold.paid + totalSold.pending + totalSold.inPacks}</span> unités
              </p>
            </div>
            {nextBreakEvenPoint && (
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Prochain seuil de rentabilité: {nextBreakEvenPoint.breakEvenPoint} ventes payées</span>
                  <span>{totalSold.paid} / {nextBreakEvenPoint.breakEvenPoint}</span>
                </div>
                <div className="flex space-x-1">
                  {/* Barre des ventes payées */}
                  <div 
                    className="bg-green-500 h-2.5 rounded-l-full" 
                    style={{ width: `${Math.min(100, (totalSold.paid / nextBreakEvenPoint.breakEvenPoint) * 100)}%` }}
                  />
                  {/* Barre des ventes en attente */}
                  {totalSold.pending > 0 && (
                    <div 
                      className="bg-yellow-400 h-2.5" 
                      style={{ width: `${Math.min(100, (totalSold.pending / nextBreakEvenPoint.breakEvenPoint) * 100)}%` }}
                    />
                  )}
                  {/* Barre des ventes via packs */}
                  {totalSold.inPacks > 0 && (
                    <div 
                      className="bg-blue-400 h-2.5" 
                      style={{ width: `${Math.min(100, (totalSold.inPacks / nextBreakEvenPoint.breakEvenPoint) * 100)}%` }}
                    />
                  )}
                  {/* Barre grise pour le reste */}
                  <div 
                    className="bg-gray-300 h-2.5 rounded-r-full" 
                    style={{ 
                      width: `${Math.max(0, 100 - ((totalSold.paid + totalSold.pending + totalSold.inPacks) / nextBreakEvenPoint.breakEvenPoint) * 100)}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Ventes payées: {totalSold.paid}</span>
                  {totalSold.pending > 0 && <span>En attente: {totalSold.pending}</span>}
                  {totalSold.inPacks > 0 && <span>Via packs: {totalSold.inPacks}</span>}
                  <span>Reste: {Math.max(0, nextBreakEvenPoint.breakEvenPoint - (totalSold.paid + totalSold.pending + totalSold.inPacks))}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Nouveau coût</h3>
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Quantité</label>
              <input
                type="number"
                value={newCost.quantity}
                onChange={(e) => setNewCost({ ...newCost, quantity: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                placeholder="Quantité"
                min="1"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700">Coût (€)</label>
              <input
                type="number"
                step="0.01"
                value={newCost.cost}
                onChange={(e) => setNewCost({ ...newCost, cost: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                placeholder="Coût"
                min="0.01"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleAddCost}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Coûts actuels</h3>
          <div className="space-y-2">
            {lotsWithProfitability.map((lot) => {
              const isMostProfitable = lot.profitability === mostProfitableLot.profitability;
              const requiredSales = calculateRequiredSales(lot);
              const isProfitable = lot.breakEvenPoint <= totalSold.paid;
              const salesNeeded = requiredSales ? requiredSales - totalSold.paid : 0;
              
              return (
                <div key={lot.quantity} className={`flex items-center justify-between p-3 rounded-lg ${isProfitable ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div>
                    <span className="font-medium">{lot.quantity} unités -</span>
                    <span className="font-medium ml-2">{lot.cost} €</span>
                    <span className="text-gray-500 ml-2">
                      ({(lot.cost)/lot.quantity}€ par unité)
                    </span>
                    <span className={`ml-2 text-sm ${isProfitable ? 'text-green-600' : 'text-gray-500'}`}>
                      Seuil de rentabilité: {lot.breakEvenPoint} ventes
                    </span>
                    {isProfitable && (
                      <div className="mt-1">
                        <span className={`text-sm ${isMostProfitable ? 'text-green-600 font-semibold' : 'text-gray-600'}`}>
                          {isMostProfitable ? 'Lot le plus rentable actuellement' : `Rentabilité: ${lot.profitability.toFixed(2)}€`}
                        </span>
                        {requiredSales && salesNeeded > 0 && (
                          <span className="text-sm text-gray-500 ml-2">
                            (Devient le plus rentable dans {salesNeeded} ventes)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveCost(lot.quantity)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section de simulation */}
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-medium mb-4">Simulation de rentabilité</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre de ventes à simuler
            </label>
            <input
              type="number"
              value={simulationSales}
              onChange={(e) => setSimulationSales(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              min="0"
            />
          </div>

          <div className="space-y-4">
            {lotsWithProfitability.map((lot) => {
              const simulatedProfitability = calculateSimulatedProfitability(lot, simulationSales);
              const isMostProfitable = simulatedProfitability === getMostProfitableLotForSales(simulationSales).simulatedProfitability;
              const numberOfLots = Math.ceil(simulationSales / lot.quantity);
              
              return (
                <div key={lot.quantity} className={`p-3 rounded-lg ${simulatedProfitability > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-medium">{lot.quantity} unités</span>
                      <span className="text-gray-500 ml-2">
                        ({(lot.cost)/lot.quantity}€ par unité)
                      </span>
                      <span className="text-sm text-gray-500 ml-2">
                        ({numberOfLots} lot{numberOfLots > 1 ? 's' : ''} nécessaire{numberOfLots > 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={`font-medium ${isMostProfitable ? 'text-green-600' : 'text-gray-600'}`}>
                        {simulatedProfitability.toFixed(2)}€
                      </span>
                      {isMostProfitable && (
                        <span className="ml-2 text-sm text-green-600">(Plus rentable)</span>
                      )}
                    </div>
                  </div>
                  {simulatedProfitability > 0 && (
                    <div className="mt-2 text-sm text-gray-500">
                      Seuil de rentabilité atteint à {lot.breakEvenPoint} ventes
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {optimalQuantity.quantity > 0 && (
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-green-800 mb-2">Recommandation d'achat</h3>
            <p className="text-green-700">
              Pour {totalSold.paid} ventes payées, il est recommandé d'acheter {optimalQuantity.quantity} unités
              à {optimalQuantity.cost.toFixed(2)}€ par unité.
            </p>
            <p className="text-green-700 mt-2">
              Seuil de rentabilité atteint: {Math.ceil(optimalQuantity.breakEvenPoint)} ventes payées
            </p>
            {mostProfitableLot && mostProfitableLot.profitability > 0 && (
              <p className="text-green-700 mt-2">
                Le lot le plus rentable actuellement est celui de {mostProfitableLot.quantity} unités
                à {(mostProfitableLot.cost / mostProfitableLot.quantity).toFixed(2)}€ par unité,
                avec une rentabilité de {mostProfitableLot.profitability.toFixed(2)}€.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProductCard = ({ 
  product, 
  onEdit, 
  onDelete, 
  onToggleAvailability,
  onManageCosts
}: { 
  product: Product;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onToggleAvailability: (id: string, is_available: boolean) => void;
  onManageCosts: (product: Product) => void;
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center space-x-4">
        <img 
          src={product.image_url} 
          alt={product.name} 
          className="w-20 h-20 object-cover rounded-lg"
        />
        <div className="flex-1">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{product.name}</h3>
              <p className="text-gray-500 text-sm">
                {product.category === 'merch' ? 'Merchandising' : 
                 product.category === 'drinks' ? 'Forfait Boissons' : 'Pack'}
              </p>
            </div>
            <div className="text-right">
              <span className="text-lg font-semibold">{product.price.toFixed(2)}€</span>
              {product.category === 'packs' && (
                <div className="text-sm text-gray-500">
                  <span className="text-green-600">Pack d'articles</span>
                </div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">{product.description}</p>
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center space-x-2">
              <span className={`w-3 h-3 rounded-full ${product.is_available ? 'bg-green-400' : 'bg-red-400'}`} />
              <span className="text-sm text-gray-600">
                {product.is_available ? 'Disponible' : 'Non disponible'}
              </span>
              <span className="text-sm text-gray-600 ml-4">
                Stock: {product.stock}
              </span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onManageCosts(product)}
                className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200 transition-colors"
              >
                Coûts
              </button>
              <button
                onClick={() => onToggleAvailability(product.id, !product.is_available)}
                className={`px-3 py-1 rounded-lg text-sm ${
                  product.is_available
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                } transition-colors`}
              >
                {product.is_available ? 'Désactiver' : 'Activer'}
              </button>
              <button
                onClick={() => onEdit(product)}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
              >
                Modifier
              </button>
              <button
                onClick={() => onDelete(product.id)}
                className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [managingCostsProduct, setManagingCostsProduct] = useState<Product | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authService.isLoggedIn()) {
      navigate('/admin/login');
      return;
    }

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const fetchedProducts = await productService.getAllProducts();
        setProducts(fetchedProducts);
      } catch (err) {
        setError('Erreur lors de la récupération des produits');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [navigate]);

  const handleCreateProduct = async (formData: ProductFormData) => {
    try {
      const newProduct = await productService.createProduct({
        ...formData,
        is_available: true,
        is_pack: formData.category === 'packs',
        costs: []
      });
      setProducts([...products, newProduct]);
      setShowForm(false);
    } catch (err) {
      setError('Erreur lors de la création du produit');
      console.error(err);
    }
  };

  const handleUpdateProduct = async (formData: ProductFormData) => {
    if (!editingProduct) return;

    try {
      const updatedProduct = await productService.updateProduct(editingProduct.id, {
        ...formData,
        is_pack: formData.category === 'packs',
        costs: editingProduct.costs || []
      });
      setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
      setShowForm(false);
      setEditingProduct(null);
    } catch (err) {
      setError('Erreur lors de la mise à jour du produit');
      console.error(err);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await productService.deleteProduct(id);
      setProducts(products.filter(p => p.id !== id));
    } catch (err) {
      setError('Erreur lors de la suppression du produit');
    }
  };

  const handleToggleAvailability = async (id: string, is_available: boolean) => {
    try {
      const updatedProduct = await productService.toggleProductAvailability(id, is_available);
      setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    } catch (err) {
      setError('Erreur lors de la mise à jour de la disponibilité');
    }
  };

  const handleManageCosts = (product: Product) => {
    setManagingCostsProduct(product);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8 pt-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Retour au tableau de bord
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des produits</h1>
          </div>
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
          >
            Ajouter un produit
          </button>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : (
          <div className="space-y-4">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEdit}
                onDelete={handleDeleteProduct}
                onToggleAvailability={handleToggleAvailability}
                onManageCosts={handleManageCosts}
              />
            ))}
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold mb-6">
                {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
              </h2>
              <ProductForm
                onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}
                onCancel={() => {
                  setShowForm(false);
                  setEditingProduct(null);
                }}
                initialData={editingProduct ?? undefined}
              />
            </div>
          </div>
        )}

        {managingCostsProduct && (
          <ProductCosts
            product={managingCostsProduct}
            onClose={() => setManagingCostsProduct(null)}
          />
        )}
      </div>
    </div>
  );
}; 